import { useEffect, useRef } from 'react';
import { useApiClient } from '../api/client.jsx';
import { useAuth } from '../auth/AuthProvider';
import { useBrand } from '../brand/BrandProvider';
import { useMe } from '../me/MeProvider';
import { isApiErrorCode } from '../api/errors';
import { ErrorCode } from '../api/errorCodes';
import { createDebugLogger, createErrorLogger } from '../utils/debug';

const dlog = createDebugLogger('bootstrap');
const elog = createErrorLogger('bootstrap');

const ME_REFRESH_INTERVAL_MS = 5 * 60_000;

/**
 * Orchestriert den Bootstrap-Flow gegen `/v1/operator/brands` + `/v1/operator/me`.
 *
 *  - `auth.status === 'authenticated'`     → /brands laden, currentBrand picken
 *  - `currentBrandOrigin` gesetzt          → /me laden
 *  - Tab wird visible / 5 min idle         → /me revalidieren (Permission-Drift)
 *  - `auth.status === 'anonymous'`         → Brand/Me-Stores cleanen
 *
 * Triggert keine eigenen Routes — Routing-Gates (ProtectedRoute) entscheiden was rendert.
 */
export function OperatorBootstrapProvider({ children }) {
  const { fetch } = useApiClient();
  const auth = useAuth();
  const brand = useBrand();
  const me = useMe();
  const meTimer = useRef(null);

  // ── Stage 1: Brands laden ─────────────────────────────────────────────
  useEffect(() => {
    if (auth.status !== 'authenticated') {
      brand.setBrands([]);
      brand.setStatus('idle');
      return;
    }
    let cancelled = false;
    brand.setStatus('loading');
    fetch('/v1/operator/brands')
      .then((res) => {
        if (cancelled) return;
        dlog('/v1/operator/brands response', res);
        if (!res || !Array.isArray(res.brands)) {
          elog('unerwartetes Brands-Response-Shape', res);
          brand.setStatus('error');
          return;
        }
        brand.setBrands(res.brands);
        if (res.brands.length === 0) {
          brand.setStatus('no-brands');
          return;
        }
        const stored = brand.currentBrandOrigin;
        const valid = stored && res.brands.some((b) => b.brandOrigin === stored);
        if (!valid) {
          brand.setCurrentBrandOrigin(res.brands[0].brandOrigin);
        }
        brand.setStatus('ready');
      })
      .catch((err) => {
        elog('/v1/operator/brands fehlgeschlagen', err);
        if (!cancelled) brand.setStatus('error');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.status]);

  // ── Stage 2: /me laden ────────────────────────────────────────────────
  useEffect(() => {
    if (auth.status !== 'authenticated') {
      me.setMe(null);
      me.setStatus('idle');
      return;
    }
    if (!brand.currentBrandOrigin || brand.status !== 'ready') return;

    let cancelled = false;
    me.setStatus('loading');
    dlog('fetching /v1/operator/me with Brand-Origin:', brand.currentBrandOrigin);
    fetch('/v1/operator/me')
      .then((res) => {
        if (cancelled) return;
        dlog('/v1/operator/me response', res);
        me.setMe(res);
        me.setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        elog('/v1/operator/me fehlgeschlagen', err);
        if (isApiErrorCode(err, ErrorCode.TenantNotInMembership)) {
          brand.setStatus('idle');
        }
        me.setStatus('error');
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.status, brand.currentBrandOrigin, brand.status]);

  // ── Permission-Drift Revalidation ─────────────────────────────────────
  useEffect(() => {
    if (auth.status !== 'authenticated' || me.status !== 'ready') return;

    const refresh = () => {
      fetch('/v1/operator/me')
        .then((res) => me.setMe(res))
        .catch(() => {
          /* best-effort */
        });
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    meTimer.current = window.setInterval(refresh, ME_REFRESH_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      if (meTimer.current) window.clearInterval(meTimer.current);
      meTimer.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.status, me.status]);

  return children;
}
